import React from 'react';
import { useSelector } from 'react-redux';
import { Provider } from 'react-redux';
import { Route, Switch } from 'react-router-dom';
import { ConnectedRouter } from 'connected-react-router';
import { history } from 'app.history';
import { I18nextProvider } from 'react-i18next';
import {
  ChannelAccessPage,
  ChannelPageDecorated,
  Error404Page,
  Error500Page,
  ErrorPermissionPage,
  HomePage,
  MyVideosPageDecorated,
  RightsPage,
  SavedVideosPage,
  SearchPageDecorated,
  StartChannelPage,
  UploadPage,
  CommentsPageDecorated,
  AttachmentsPageDecorated,
} from 'pages';
import { appRoutes } from 'routes';
import { qsProps } from 'utils';
import { i18n } from 'utils/i18n/i18n';
import { WebsiteLoader } from 'components/loaders/website';
import { AdminRoute } from 'components/admin-route';
import { EditMyVideoPageDecorated } from 'pages/my-videos.page/edit.page';
import { extractTokenPayload } from 'utils/token';
import { UitArchiveDownloadPageDecorated } from 'pages/custom/uit-archive-download';
import { UitRegularDownloadPageDecorated } from 'pages/custom/uit-regular-download';
import { isMyVideosVisibleSelector } from 'common/selectors';
import { getNewRelicAgent } from 'newRelicAgent';
import { ErrorBoundary } from 'react-error-boundary';

const Router = () => {
  const isMyVideosVisible = useSelector(isMyVideosVisibleSelector);
  return (
    <Switch>
      <Route path={appRoutes.uitArchiveDownload} exact component={UitArchiveDownloadPageDecorated} />
      <Route path={appRoutes.uitRegularDownload} exact component={UitRegularDownloadPageDecorated} />
      <Route path={appRoutes.mobileAttachments} component={AttachmentsPageDecorated} />
      <Route path={appRoutes.mobileComments} component={CommentsPageDecorated} />
      <Route path={appRoutes.channel} render={(props) => <ChannelPageDecorated {...qsProps(props)} />} />
      <Route path={appRoutes.search} render={(props) => <SearchPageDecorated {...qsProps(props)} />} />
      {isMyVideosVisible && (
        <Route path={appRoutes.myVideosEdit} render={(props) => <EditMyVideoPageDecorated {...qsProps(props)} />} />
      )}
      {isMyVideosVisible && (
        <Route path={appRoutes.myVideos} render={(props) => <MyVideosPageDecorated {...qsProps(props)} />} />
      )}
      {isMyVideosVisible && (
        <Route path={appRoutes.upload} exact render={(props) => <UploadPage {...qsProps(props)} />} />
      )}
      {isMyVideosVisible && (
        <Route
          path={appRoutes.uploadPodcast}
          exact
          render={(props) => <UploadPage isPodcast={true} {...qsProps(props)} />}
        />
      )}
      <Route path={appRoutes.videoPage} exact render={(props) => <HomePage {...qsProps(props)} />} />
      <Route path={appRoutes.error500} exact component={Error500Page} />
      <Route path={appRoutes.error404} exact component={Error404Page} />
      <Route path={appRoutes.permissionError} exact component={ErrorPermissionPage} />
      <Route path={appRoutes.savedVideos} render={(props) => <SavedVideosPage {...qsProps(props)} />} />
      <Route
        path={appRoutes.homePage}
        exact
        render={(props) => {
          const ctTokenPayload = extractTokenPayload();
          const isSiteLoaded = sessionStorage.getItem('isSiteLoaded') === 'true';
          const hasStartChannel = ctTokenPayload && ctTokenPayload.start_channel_id;
          if (!isSiteLoaded && hasStartChannel) {
            window.location.href =
              '/channel/' + ctTokenPayload.start_channel_id + '/' + ctTokenPayload.start_channel_slug + '/1';
            return;
          }
          return <HomePage {...qsProps(props)} />;
        }}
      />
      <AdminRoute path={appRoutes.administration} component={RightsPage} />
      <AdminRoute path={appRoutes.channelAccess} component={ChannelAccessPage} />
      <AdminRoute path={appRoutes.startChannel} component={StartChannelPage} />
      <Route component={Error404Page} />
    </Switch>
  );
};

export const AppProvider = (store) => {
  const logError = (error) => {
    getNewRelicAgent().noticeError(error);
  };

  return (
    <ErrorBoundary onError={logError} fallback={<div>Unexpected error happend</div>}>
      <Provider store={store}>
        <I18nextProvider i18n={i18n}>
          <ConnectedRouter history={history}>
            <WebsiteLoader>
              <Router />
            </WebsiteLoader>
          </ConnectedRouter>
        </I18nextProvider>
      </Provider>
    </ErrorBoundary>
  );
};
